import { v4 as uuidv4 } from "uuid";

import {
  ddpVersionOptions, ipVersionOptions,
  scopeLevelOption, defaultIpModeState, defaultLayer3Type, protocolTypeOption,
  otherTimeout,
} from "./const";
import { getSubnetInfo } from "utils/network-validation";

export const getAPIKeyAndParams = (auth, path, specificSiteId) => {
  const definedPath = {
    sites: {
      org: '/orgs/sites.read',
      site: '/orgs/sites.read',
    },
    inventoryDeviceAccount: {
      org: '/orgs/settings/inventoryDeviceAccount.read',
      site: '/orgs/sites/devices/inventoryDeviceAccount.read',
    },
    deviceAccount: {
      org: '/orgs/sites/settings/deviceAccount.read',
      site: '/orgs/sites/settings/deviceAccount.read',
    },
    existedDevices: {
      org: '/orgs/existedDevices.read',
      site: '/orgs/sites/existedDevices.read',
    },
    inventoryDevices: {
      org: "/orgs/devices/inventory.read",
      site: "/orgs/sites/devices/inventory.read",
    },
    discovery: {
      org: '/orgs/devices/provision/discovery.read',
      site: '/orgs/sites/devices/provision/discovery.read',
    },
    stopDiscovery: {
      org: '/orgs/devices/provision/stop.read',
      site: '/orgs/sites/devices/provision/stop.read',
    },
    authenticate: {
      org: '/orgs/devices/provision/authenticate.read',
      site: '/orgs/sites/devices/provision/authenticate.read',
    },
    ipSetting: {
      org: '/orgs/devices/provision/ipSetting.read',
      site: '/orgs/sites/devices/provision/ipSetting.read',
    },
    adopt: {
      org: '/orgs/devices/provision/adopt.read',
      site: '/orgs/devices/provision/adopt.read',
      // Phase out
      // site: '/orgs/sites/devices/provision/adopt.read',
    },
    preConfirm: {
      org: '/orgs/license/service/subscribe/preConfirm',
      site: '/orgs/license/service/subscribe/preConfirm',
    },
  };

  const { scopeLevel, realm, orgs, sites } = auth;
  const levelId = { realm: realm?.id, org: orgs?.id, site: sites?.id };
  const isStandAloneOrg = !levelId.org || Object.keys(orgs)?.length === 0;
  const apiKey = isStandAloneOrg ? `${definedPath[path][scopeLevel]}` : `/ents${definedPath[path][scopeLevel]}`;
  const placeholder = isStandAloneOrg ? { orgId: levelId.realm } : { entId: levelId.realm, orgId: levelId.org };
  let ignoreScopeLevel = specificSiteId ? true : false;

  if (ignoreScopeLevel) {
    placeholder.siteId = specificSiteId;
  }

  if (!ignoreScopeLevel && scopeLevel === scopeLevelOption.site) {
    placeholder.siteId = levelId.site;
  }

  return { apiKey, placeholder };
}

export const makeDiscoveryPayload = ({ auth, layerSetting, selectedProxyDevice }) => {
  let layers = [];
  if (layerSetting.layer2.value) {
    ddpVersionOptions.forEach((ddp) => {
      layers.push({
        ddpVersion: ddp,
        layer: 2,
      });
    });
  }

  if (layerSetting.layer3.value) {
    ddpVersionOptions.forEach((ddp) => {
      const ipList = [];
      for (let i = 0; i < layerSetting.layer3.configures.length; i++) {
        const config = layerSetting.layer3.configures[i];
        if (config.type.value === defaultLayer3Type.ip.value && config.startIp && config.endIp) {
          ipList.push({ fromIp: config.startIp, toIp: config.endIp });
        } else if (config.type.value === defaultLayer3Type.subnet.value && config.range) {
          const cidr = `${config.startIp}/${config.range}`;
          const { broadcast } = getSubnetInfo(cidr);
          ipList.push({ fromIp: config.startIp, toIp: broadcast });
        }
      }

      ipVersionOptions.forEach((ipVersion) => {
        layers.push({
          ddpVersion: ddp,
          layer: 3,
          ipVersion,
          ipList,
        });
      });
    });
  }

  const newSocketId = uuidv4();
  const { apiKey, placeholder } = getAPIKeyAndParams(auth, 'discovery');

  const payload = {
    apiKey,
    socketId: newSocketId,
    placeholder,
    params: {
      action: "start",
      timeout: otherTimeout,
      data: layers,
    }
  }

  if (selectedProxyDevice) {
    payload.params.proxyUuid = selectedProxyDevice.proxyUuid;
  }

  return payload;
}

export const makeStopDiscoverPayload = ({ auth }) => {
  const newSocketId = uuidv4();
  const { apiKey, placeholder } = getAPIKeyAndParams(auth, 'stopDiscovery');

  const params = {
    action: "stop",
    timeout: otherTimeout,
  }

  return {
    apiKey,
    socketId: newSocketId,
    placeholder,
    params,
  }
}

export const makeAdoptDevicePayload = ({
  auth,
  selectedSiteId, selectedDevice,
  sshUsername, sshPassword
}) => {
  const newSocketId = uuidv4();
  const { apiKey, placeholder } = getAPIKeyAndParams(auth, 'adopt', selectedSiteId);

  let setAgProfile = true;
  let username = selectedDevice.username;
  let password = selectedDevice.password;
  if (selectedDevice.protocolType === protocolTypeOption.snmp) {
    if (sshUsername && sshPassword) {
      setAgProfile = false;
      username = sshUsername;
      password = sshPassword;
    }
  }

  const params = {
    siteId: selectedSiteId === 'inventory' ? null : selectedSiteId,
    devices: [
      {
        modelName: selectedDevice.modelName,
        hardwareVersion: selectedDevice.hdVersion,
        deviceId: selectedDevice.macAddress,
        productCategory: selectedDevice.deviceType.value,
        version: selectedDevice.version,
        protocolType: selectedDevice.protocolType,
        localIp: selectedDevice.ipAddress,
        deviceAccount: {
          username,
          password,
        },
        ddp: {
          setAgProfile,
          layer: selectedDevice.layer,
          ddpVersion: selectedDevice.ddpVersion,
        }
      }
    ]
  };

  return {
    apiKey,
    socketId: newSocketId,
    placeholder,
    params,
  }
}

export const makeIpSettingDevicesPayload = ({
  auth,
  selectedDevices,
  selectedIpMode,
  config,
  isNotSupportDNSDevice,
}) => {

  const newSocketId = uuidv4();
  const { apiKey, placeholder } = getAPIKeyAndParams(auth, 'ipSetting');

  const params = {
    timeout: otherTimeout,
    data: []
  };

  if (selectedIpMode.value === defaultIpModeState.static.value) {
    for (let i = 0; i < selectedDevices.length; i++) {
      const device = selectedDevices[i];

      if (device.layer === 2) {
        params.data.push(
          {
            layer: 2,
            ddpVersion: device.ddpVersion,
            destMacAddress: device.macAddress,
            username: device.username,
            password: device.password,
            deviceIpType: 0,
            dhcpEnable: 0,
            deviceIp: config.ipAddress,
            subnetMask: config.subnetMask,
            defaultGateway: config.gateway,
            deviceDNSType: isNotSupportDNSDevice(device.modelName) ? null : 0,
            primaryDNS: isNotSupportDNSDevice(device.modelName) ? null : config.dns1,
            secondaryDNS: isNotSupportDNSDevice(device.modelName) ? null : config.dns2,
          }
        );
      } else {
        params.data.push(
          {
            layer: 3,
            ipVersion: device.ipVersion,
            ddpVersion: device.ddpVersion,
            destMacAddress: device.macAddress,
            destIpAddress: device.ipAddress,
            username: device.username,
            password: device.password,
            deviceIpType: 0,
            dhcpEnable: 0,
            deviceIp: config.ipAddress,
            subnetMask: config.subnetMask,
            defaultGateway: config.gateway,
            deviceDNSType: isNotSupportDNSDevice(device.modelName) ? null : 0,
            primaryDNS: isNotSupportDNSDevice(device.modelName) ? null : config.dns1,
            secondaryDNS: isNotSupportDNSDevice(device.modelName) ? null : config.dns2,
          }
        );
      }
    }
  } else {
    for (let i = 0; i < selectedDevices.length; i++) {
      const device = selectedDevices[i];

      if (device.layer === 2) {
        params.data.push({
          layer: 2,
          ddpVersion: device.ddpVersion,
          destMacAddress: device.macAddress,
          username: device.username,
          password: device.password,
          deviceIpType: 0,
          dhcpEnable: 1,
          specifyCustomizedDNS: isNotSupportDNSDevice(device.modelName) ? null : 0,
        });
      } else {
        params.data.push({
          layer: 3,
          ipVersion: device.ipVersion,
          ddpVersion: device.ddpVersion,
          destMacAddress: device.macAddress,
          destIpAddress: device.ipAddress,
          username: device.username,
          password: device.password,
          deviceIpType: 0,
          dhcpEnable: 1,
          specifyCustomizedDNS: isNotSupportDNSDevice(device.modelName) ? null : 0,
        });
      }
    }
  }

  return {
    apiKey,
    socketId: newSocketId,
    placeholder,
    params,
  };
}

export const makeAuthenticateDevicesPayload = ({ auth, selectedDevices, username, password }) => {
  const newSocketId = uuidv4();
  const { apiKey, placeholder } = getAPIKeyAndParams(auth, 'authenticate');
  const params = {
    timeout: otherTimeout,
    data: []
  };

  for (let i = 0; i < selectedDevices.length; i++) {
    const device = selectedDevices[i];
    params.data.push({
      layer: device.layer,
      ddpVersion: device.ddpVersion,
      ipVersion: device.ipVersion,
      destMacAddress: device.macAddress,
      destIpAddress: device.ipAddress,
      username,
      password,
    });
  };

  return {
    apiKey,
    socketId: newSocketId,
    placeholder,
    params,
  };
}

export const makeAdoptDevicesPayload = ({
  auth, selectedSiteId, selectedDevices,
  sshUsername, sshPassword,
}) => {
  const newSocketId = uuidv4();
  const { apiKey, placeholder } = getAPIKeyAndParams(auth, 'adopt', selectedSiteId);

  const params = {
    siteId: selectedSiteId === 'inventory' ? null : selectedSiteId,
    devices: [],
  };

  for (let i = 0; i < selectedDevices.length; i++) {
    const device = selectedDevices[i];

    let setAgProfile = true;
    let username = device.username;
    let password = device.password;
    if (device.protocolType === protocolTypeOption.snmp) {
      setAgProfile = false;
      username = sshUsername;
      password = sshPassword;
    }
    const data = {
      modelName: device.modelName,
      hardwareVersion: device.hdVersion,
      deviceId: device.macAddress,
      productCategory: device.deviceType.value,
      version: device.version,
      protocolType: device.protocolType,
      localIp: device.ipAddress,
      deviceAccount: {
        username,
        password,
      },
      ddp: {
        setAgProfile,
        layer: device.layer,
        ddpVersion: device.ddpVersion,
      }
    };

    params.devices.push(data);
  };

  return {
    apiKey,
    socketId: newSocketId,
    placeholder,
    params,
  };
}